// web/src/components/ShareMatchModal.tsx
// "Send to family" modal (Horizon 1 / H1.2). Augments the existing Web
// Share API flow on MatchDetail.
//
// Three options:
//   1. Copy match link (existing share-token flow)
//   2. Email a recap to one address (new — fires sendToFamily API)
//   3. Open OS share sheet (existing Web Share API)
//
// Server enforces 5-recipient daily cap per match. UI surfaces the 429
// error inline; the modal stays open so the user knows what happened.

import { useState } from 'react';
import { useMatchApi } from '../api/match';
import { Button } from './Button';
import { ApiError } from '../api/client';

interface Props {
  kidId: string;
  matchId: string;
  /** Pre-resolved share URL (caller already created the share token). */
  shareUrl: string;
  /** Native Web Share API handler — falls back to clipboard inside the modal. */
  onOpenSystemShare: () => void;
  /** Caller controls visibility. */
  onClose: () => void;
}

export function ShareMatchModal({ kidId, matchId, shareUrl, onOpenSystemShare, onClose }: Props) {
  const api = useMatchApi();
  const [mode, setMode] = useState<'menu' | 'email'>('menu');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      window.setTimeout(() => setCopiedLink(false), 1800);
    } catch {
      // Older browsers: degrade by selecting in an offscreen textarea.
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedLink(true);
      window.setTimeout(() => setCopiedLink(false), 1800);
    }
  };

  const sendEmail = async () => {
    if (sending) return;
    setError(null);
    const email = recipientEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 254) {
      setError('Enter a valid email address.');
      return;
    }
    setSending(true);
    try {
      const res = await api.sendToFamily(kidId, matchId, email);
      setSentTo(res.sentTo);
      setRecipientEmail('');
    } catch (err) {
      const e = err as ApiError;
      if (e.status === 429) {
        setError(e.message || 'You\'ve hit the daily limit for sending this match.');
      } else if (e.status === 400) {
        setError(e.message || 'That email looks wrong — give it another go.');
      } else if (e.status === 502 || e.status === 503) {
        setError('Couldn\'t send right now. Try again in a moment.');
      } else {
        setError(e.message || 'Something went wrong.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        background: 'rgba(20, 24, 26, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(420px, 100%)',
          background: 'var(--surface)',
          borderRadius: 16,
          padding: 'var(--space-lg)',
          boxShadow: '0 18px 48px rgba(0,0,0,0.25)',
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            border: 'none',
            borderRadius: '50%',
            background: 'transparent',
            color: 'var(--muted)',
            cursor: 'pointer',
            fontSize: 18,
          }}
        >
          ✕
        </button>

        {mode === 'menu' ? (
          <>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 'var(--micro)', letterSpacing: '0.22em',
              textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700, marginBottom: 6,
            }}>
              Share this match
            </div>
            <h2 style={{
              fontFamily: 'var(--font-masthead)', fontStyle: 'italic', textTransform: 'uppercase',
              letterSpacing: '-0.01em', fontSize: '1.6rem', lineHeight: 1.05, margin: '0 0 16px',
            }}>
              Send to family
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Button label={copiedLink ? 'Link copied ✓' : 'Copy match link'} onClick={copyLink} variant="ghost" />
              <Button label="Email a recap" onClick={() => setMode('email')} />
              <Button label="Open share sheet" onClick={onOpenSystemShare} variant="ghost" />
            </div>

            <p style={{ marginTop: 16, color: 'var(--muted)', fontSize: 13, lineHeight: 1.5 }}>
              Emailing a recap sends a magazine-styled summary directly to one address. Reply-to is your own email so grandma can hit reply.
            </p>
          </>
        ) : (
          <>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 'var(--micro)', letterSpacing: '0.22em',
              textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700, marginBottom: 6,
            }}>
              Email a recap
            </div>
            <h2 style={{
              fontFamily: 'var(--font-masthead)', fontStyle: 'italic', textTransform: 'uppercase',
              letterSpacing: '-0.01em', fontSize: '1.4rem', lineHeight: 1.05, margin: '0 0 16px',
            }}>
              One recipient at a time
            </h2>

            {sentTo ? (
              <div
                style={{
                  padding: 12, borderRadius: 12, background: 'var(--green)', color: 'var(--ink-on-green)',
                  fontSize: 14, marginBottom: 12,
                }}
              >
                Sent to <strong>{sentTo}</strong>. Send to another?
              </div>
            ) : null}

            <label style={{ display: 'block', fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>
              Recipient email
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => { setRecipientEmail(e.target.value); setError(null); setSentTo(null); }}
              placeholder="grandma@example.com"
              autoComplete="email"
              disabled={sending}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${error ? 'var(--result-loss)' : 'var(--border)'}`,
                background: 'var(--cream)',
                color: 'var(--ink)',
                fontSize: 15,
                marginBottom: 8,
                boxSizing: 'border-box',
              }}
            />
            {error ? (
              <div style={{ color: 'var(--result-loss)', fontSize: 13, marginBottom: 12 }}>{error}</div>
            ) : null}

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <Button label="Back" onClick={() => setMode('menu')} variant="ghost" />
              <Button label={sending ? 'Sending…' : 'Send recap'} onClick={sendEmail} disabled={sending || !recipientEmail.trim()} />
            </div>
            <p style={{ marginTop: 16, color: 'var(--muted)', fontSize: 12, lineHeight: 1.5 }}>
              Up to 5 sends per match per day to discourage abuse. No address-book lookup, no CC list.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
